import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { GET_JOBS } from '../../../graphql/queries';
import { formatInTimeZone } from 'date-fns-tz';
import { 
  convertTo24HourFormat, 
  createTimezoneSafeDate 
} from '../utils/timeUtils';
import { 
  createJobInput, 
  createManualJobData 
} from '../utils/jobUtils';

// Create a simplified mutation that doesn't request the property field
const CREATE_JOB_MINIMAL = gql`
  mutation CreateCustomJob($input: CreateCustomJobInput!) {
    createCustomJob(input: $input) {
      id
      category
      status
      propertyId
      scheduledDate
      startTime
      notes
      payout
      billedAmount
    }
  }
`;

/**
 * Custom hook for job creation functionality
 * @param {Function} onSuccess - Callback function on successful job creation
 * @param {Function} onError - Callback function on job creation error
 * @returns {Object} Job creation state and functions
 */
export default function useJobCreation(onSuccess, onError) {
  const [isCreating, setIsCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [createJob, { loading: createLoading }] = useMutation(CREATE_JOB_MINIMAL, {
    onCompleted: (data) => {
      console.log('Job created successfully with minimal data:', data);
      
      if (!data || !data.createCustomJob) {
        console.error('Job created but no data returned');
        setErrorMessage('Job was created but the server returned incomplete data. Please refresh the page.');
        onError?.('Incomplete data returned');
        return;
      }
      
      setSuccessMessage('Job created successfully!');
      onSuccess?.(data.createCustomJob);
    },
    onError: (error) => {
      console.error('Error creating job:', error);
      
      // Special handling for property-related errors that we expect
      if (error.graphQLErrors) {
        const nonNullablePropertyError = error.graphQLErrors.find(
          e => e.message.includes('Cannot return null for non-nullable field Job.property')
        );
        
        if (nonNullablePropertyError) {
          console.log('Detected the expected property null error. The job was likely created but the GraphQL response failed.');
          
          // Extract the job ID from the error path if available
          const jobId = nonNullablePropertyError.path?.[1];
          
          if (jobId) {
            console.log('Job was created with ID:', jobId);
            setSuccessMessage('Job created successfully!');
            onSuccess?.({ id: jobId });
            return;
          }
        }
        
        // Handle other GraphQL errors
        const propertyError = error.graphQLErrors.find(e => 
          e.message.includes('property') || 
          (e.path && e.path.includes('property'))
        );
        
        if (propertyError) {
          console.error('Property-related error detected:', propertyError);
          const errorMsg = `Error with property data: ${propertyError.message}. Please try selecting a different property.`;
          setErrorMessage(errorMsg);
          onError?.(errorMsg);
          return;
        }
      }
      
      // Generic error handling
      let errorMsg = "Failed to create job. ";
      
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMsg += error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMsg += "Network error. Please check your connection.";
      } else {
        errorMsg += error.message;
      }
      
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    },
    refetchQueries: [
      { 
        query: GET_JOBS,
        variables: {
          input: {
            startDate: formatInTimeZone(
              new Date(),
              'America/New_York',
              'yyyy-MM-dd\'T\'00:00:00.000XXX'
            ),
            limit: 100,
            offset: 0
          }
        },
        fetchPolicy: 'network-only'
      }
    ]
  });

  /**
   * Create a regular job
   * @param {Object} formData - Form data for job creation
   * @param {Object} selectedProperty - Selected property object
   */
  const createRegularJob = async (formData, selectedProperty) => {
    setIsCreating(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Convert time format
      const formattedStartTime = convertTo24HourFormat(formData.startTime);
      
      // Create timezone-safe date
      const adjustedScheduledDate = createTimezoneSafeDate(formData.scheduledDate);
      
      console.log('Creating job with validated input:', {
        ...formData,
        startTime: formattedStartTime,
        scheduledDate: adjustedScheduledDate
      });

      const jobInput = createJobInput({
        ...formData,
        startTime: formattedStartTime,
        scheduledDate: adjustedScheduledDate
      });

      console.log('Final job input being sent to backend:', jobInput);

      await createJob({
        variables: {
          input: jobInput
        }
      });
    } catch (error) {
      console.error('Error in createRegularJob:', error);
      setErrorMessage(error.message);
      onError?.(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Clear success and error messages
   */
  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  return {
    // State
    isCreating: isCreating || createLoading,
    successMessage,
    errorMessage,
    
    // Actions
    createRegularJob,
    clearMessages
  };
} 